/**
 * Design Center category definitions for fixtures and surfaces.
 * Fixture swap routing uses CLOUD_SWAP_FIXTURE_TYPES from fixtureSwapTypes.js
 * (the same source of truth as StructureViewerWrapper and other panels).
 */

const {
  isVanityAssemblyLight,
  roomHasVanity: roomHasVanityFromObjects,
} = require('../utils/vanityAssemblyMatchers');
const { findRoomVanityCabinet } = require('../../vanity-assembly/fixtureGroup');
const { primaryRoomKind } = require('../../rooms/roomKind');
const {
  resolvePendantAnchors,
} = require('../../kitchen-assembly/styling/pendantPlacement');

/**
 * A kitchen pendant: the canonical `ceiling-light` carrying the catalog's own
 * `lightingSubtype`. Recessed cans and drums share the type and carry none, so
 * the subtype is what keeps this row off the room's ambient lighting.
 */
const isKitchenPendant = (obj) =>
  obj?.type === 'ceiling-light' && obj?.lightingSubtype === 'pendant';

// Fixture categories shown in the Design Center, with the object types
// that belong to each category.
//
// Flags:
//   isAccessory  — part of a parent assembly; "× Remove" is hidden because
//                  remove_fixture would delete the whole assembly via the
//                  shared fixtureGroupId. Per-accessory removal is a v2 item.
//   isProcedural — assembled procedurally (no single catalog model). The UI
//                  shows an "Add" CTA instead of a tile carousel when empty,
//                  and a status-only row (no carousel) when present.
//   match        — optional `(obj, allObjects) => boolean` used in place of
//                  the default `types.includes(obj.type)` filter. Required
//                  when membership depends on cross-object state (e.g.
//                  vanity-assembly affiliation for the wall-light category).
//   roomKinds    — Set of room kinds (from `primaryRoomKind`) where this
//                  category is allowed to surface. Without it, a kitchen's
//                  `lower-cabinet` would falsely show as "Vanity", and a
//                  kitchen sink would show under the bathroom-only sink
//                  category. Every entry names its kinds explicitly; the
//                  bathroom and kitchen sets are disjoint.
/**
 * How many tiles a main-panel carousel aims to show.
 *
 * Curated lists are edited by hand at `/admin/curated-designs`, and some ran short of
 * the catalog they were curating from: Countertop shipped 3 of its 4
 * textures, Cabinets 2 of 5 — so a third of the countertop choice sat behind
 * a "See All" button that opened a grid of four. When a curated list is
 * shorter than this, the panel tops it up from the full catalog and drops the
 * See All button once every catalog item is on screen.
 *
 * 8 is the size the wall and floor lists are already curated to, so those two
 * are unaffected and no catalog fetch fires for them.
 *
 * iOS twin: `DesignPickerCategories.carouselTarget`. Keep them equal — they
 * decide the same thing on two clients looking at one Firestore document.
 */
const CAROUSEL_TARGET = 8;

// Design Center surface key -> the `surfaceType` that `getApplicationType`
// understands. Used by both the See All catalog load and the small-catalog
// top-up; they must agree or the two would size and fetch different lists.
const SURFACE_APPLICATION = {
  wall: 'wall',
  floor: 'floor',
  counter: 'countertop',
  cabinet: 'cabinet',
  // Shower Tile is its own catalog, NOT the bathroom-wall one. Every curated
  // tile the shower carousel shows is ShowerWall-rated and at least one is
  // not BathroomWall-rated, so querying the wall catalog for its "See All"
  // drops shower-rated tiles — including one visible in the carousel right
  // above the button. 148 ShowerWall vs 135 BathroomWall in dev, overlapping
  // 131.
  showerWall: 'showerWall',
  // Same story for the floor: the shower floor is a small-format,
  // slip-resistant family (36 in dev), not the room's floor slab.
  showerFloor: 'showerFloor',
  // The bathroom bands the wall panel's Backsplash card tiles — no Fixtures &
  // Finishes row of their own, so the catalogs Edit wall has always used for
  // the surface: a vanity's splash is backsplash tile (BathroomBacksplash +
  // Countertop in a bathroom), a tub's is BathtubSurround.
  vanityBacksplash: 'backsplash',
  tubBacksplash: 'tubBacksplash',
};

/**
 * Shower hardware that belongs to a TUB rather than to a stall — the two
 * objects that make a tub/shower combo. A walk-in shower's own head and valve
 * share the SHOWER's fixture group, so they must not light up the combo row.
 */
const isTubShowerHardware = (obj, allObjects = []) =>
  (obj?.type === 'showerhead' || obj?.type === 'shower-controls') &&
  !!obj.fixtureGroupId &&
  allObjects.some(
    (o) =>
      (o?.type === 'bathtub' || o?.type === 'tub') &&
      o.fixtureGroupId === obj.fixtureGroupId
  );

const FIXTURE_CATEGORIES = [
  {
    key: 'vanity',
    label: 'Vanity',
    // Detect the vanity assembly via its cabinet member.
    types: ['lower-cabinet'],
    isProcedural: true,
    roomKinds: new Set(['bathroom']),
  },
  {
    key: 'sink',
    label: 'Sink',
    types: ['sink'],
    isAccessory: true,
    roomKinds: new Set(['bathroom']),
  },
  {
    key: 'faucet',
    label: 'Faucet',
    types: ['faucet', 'bathroom-sink-faucet', 'bathroom-faucet'],
    isAccessory: true,
    roomKinds: new Set(['bathroom']),
  },
  {
    key: 'tub-faucet',
    label: 'Tub Faucet',
    types: ['bathroom-tub-faucet'],
    isAccessory: true,
    roomKinds: new Set(['bathroom']),
  },
  {
    key: 'toilet',
    label: 'Toilet',
    types: ['toilet'],
    roomKinds: new Set(['bathroom']),
  },
  {
    key: 'bathtub',
    label: 'Bathtub',
    types: ['bathtub', 'tub'],
    roomKinds: new Set(['bathroom']),
  },
  {
    key: 'shower',
    label: 'Shower',
    // A walk-in stall, and ONLY that. A tub/shower combo used to count as a
    // shower here (`showerhead` was a member type) purely to suppress
    // "+ Add Shower" on a room that had one — but that also meant the combo
    // reported as a shower this row could neither swap nor remove. The combo
    // has its own row now, so this one is the stall: `remove_fixture(shower)`
    // tears down a generated assembly by fixtureGroupId, and a `shower`
    // object is exactly what it can act on.
    types: ['shower'],
    isProcedural: true,
    roomKinds: new Set(['bathroom']),
  },
  {
    key: 'tub-shower',
    label: 'Shower Over Tub',
    // The combo is not a TYPE — no object is ever saved as `tub-shower`.
    // It is a showerhead / valve sharing a bathtub's fixture group, which is
    // how every other consumer recognises one (see `roomHasTubShowerCombo`).
    types: ['showerhead', 'shower-controls'],
    match: isTubShowerHardware,
    isProcedural: true,
    // Remove takes the shower OFF the tub and leaves the bath. It needs its
    // own backend branch precisely because the hardware shares the TUB's
    // fixtureGroupId, so the usual delete-the-whole-group path would take the
    // bath with it — `remove_fixture` handles `fixtureType: 'tubShower'` by
    // deleting only these two types.
    removeTypes: ['showerhead', 'shower-controls'],
    roomKinds: new Set(['bathroom']),
  },
  {
    key: 'mirror',
    label: 'Mirror',
    types: ['mirror'],
    isAccessory: true,
    roomKinds: new Set(['bathroom']),
  },
  {
    key: 'wall-light',
    label: 'Vanity Light',
    // The vanity assembly can place a sconce, an overhead bar, or a
    // ceiling pendant. `match` enforces that the light shares a
    // fixtureGroupId with a vanity in the same room — pure
    // `ceiling-light` matching would over-grab ambient room lights.
    types: ['wall-light', 'overhead-wall-light', 'ceiling-light'],
    match: isVanityAssemblyLight,
    isAccessory: true,
    roomKinds: new Set(['bathroom']),
  },
  // Showerhead: excluded for now — swap_fixture with fixtureType 'shower'
  // and no componentType rebuilds the entire shower assembly. Component-level
  // showerhead swaps need to be added to swapComponent() first.

  // ── Kitchen ───────────────────────────────────────────────────────────────
  //
  // Every kitchen category is SWAP-ONLY: none appears in
  // `CATEGORY_TO_BACKEND_FIXTURE_TYPE`, because no `place_fixture` branch can
  // add a range or a dishwasher — appliances arrive from the scan or from the
  // layout generator, which owns where they sit in the run. That absence is
  // load-bearing rather than a gap left to fill in: it is what makes
  // `FixtureSection` hide the × Remove button and what makes `resolveFixtureFit`
  // return early, so a kitchen row can never offer an action with no backend
  // behind it.
  //
  // For the same reason there is no `ALWAYS_SHOW_BY_ROOM_TYPE.kitchen`. An
  // always-shown row on a room that has none of that appliance would render
  // "none — tap any to add" over a carousel whose every tap can only toast,
  // which is the dead-control shape the shower-floor card was narrowed to
  // avoid. Kitchen rows appear when the object is in the room.
  //
  // `types` does double duty — it matches room objects AND it is the list
  // `loadFullCatalog` queries `unifiedObjects` with for "See All" — so each
  // entry carries the legacy aliases alongside the current name. Saved
  // structures predate the type-name alignment (`range-hood` → `vent-hood`,
  // `fridge` → `refrigerator`; see `openingClearance.js` and
  // `mergeKitchenObjects.js`), and `getGLBModelsForTypes` de-dupes across
  // types, so listing an alias the catalog has never used costs one empty
  // query and covers a room the current name would miss.
  {
    key: 'range',
    label: 'Range',
    types: ['range', 'stove'],
    roomKinds: new Set(['kitchen']),
  },
  {
    // A cooktop is its own catalog type, not a range without an oven — the
    // two are separately stocked and separately sized (`CATALOG_TYPE` in
    // `planApplianceModels.js` maps them apart), and a kitchen with a cooktop
    // usually has a wall oven as well. Both rows can be present at once.
    key: 'cooktop',
    label: 'Cooktop',
    types: ['cooktop'],
    roomKinds: new Set(['kitchen']),
  },
  {
    key: 'wall-oven',
    label: 'Wall Oven',
    types: ['wall-oven', 'oven'],
    roomKinds: new Set(['kitchen']),
  },
  {
    key: 'vent-hood',
    label: 'Vent Hood',
    types: ['vent-hood', 'range-hood'],
    roomKinds: new Set(['kitchen']),
  },
  {
    key: 'refrigerator',
    label: 'Refrigerator',
    types: ['refrigerator', 'fridge'],
    roomKinds: new Set(['kitchen']),
  },
  {
    key: 'dishwasher',
    label: 'Dishwasher',
    types: ['dishwasher'],
    roomKinds: new Set(['kitchen']),
  },
  {
    // The kitchen sink and faucet are the catalog's KITCHEN-specific types,
    // never the bare `sink` / `faucet` the bathroom rows use — those are
    // vessel basins and pillar taps. Keeping them apart is the whole reason
    // `roomKinds` exists on the bathroom entries.
    key: 'kitchen-sink',
    label: 'Kitchen Sink',
    types: ['kitchen-sink'],
    isAccessory: true,
    roomKinds: new Set(['kitchen']),
  },
  {
    key: 'kitchen-faucet',
    label: 'Kitchen Faucet',
    types: ['kitchen-faucet'],
    isAccessory: true,
    roomKinds: new Set(['kitchen']),
  },
  {
    // Pendants are saved as plain `ceiling-light` objects — the same type as
    // the ambient cans `add_lighting` scatters across the room (217 of them
    // across the 28 corpus kitchens) and the same type the bathroom
    // vanity-light row matches on. So the row CANNOT be keyed to the type:
    // `lightingSubtype` is the discriminator the catalog and the planner both
    // already use, and `catalogSubtype` narrows See All to it so the grid
    // offers pendants rather than every ceiling fixture Kohler sells.
    key: 'kitchen-pendant',
    label: 'Pendant Lights',
    types: ['ceiling-light'],
    catalogSubtype: { field: 'lightingSubtype', value: 'pendant' },
    match: isKitchenPendant,
    isAccessory: true,
    roomKinds: new Set(['kitchen']),
  },
];

// Object types excluded from the Design Center fixture list
const EXCLUDED_OBJECT_TYPES = new Set([
  'shower-door',
  'shower-controls',
  'shower-niche',
  'cabinet',
  'upper-cabinet',
  'countertop',
  'tub-surface',
  'tub-support-box',
  'switch',
  'ceiling-light',
  'vanity',
  'storage',
]);

// Always-show fixture types per room type.
// Mockup B requirement: Vanity / Toilet / Tub / Shower always shown for bathrooms.
// Sub-component types (Sink / Faucet / Mirror / Vanity Light) remain conditional
// on having a parent fixture in the room (handled below).
const ALWAYS_SHOW_BY_ROOM_TYPE = {
  bathroom: new Set(['vanity', 'toilet', 'bathtub', 'shower']),
};

/**
 * Component category → the category of the fixture it hangs off.
 *
 * A component's row shows when its PARENT is in the room, even with no
 * component present, so the row can offer to add one. The mirror and the
 * vanity light are the vanity's; the TUB FAUCET is the tub's, and was
 * missing — a tub with no faucet offered no way to get one, from the panel or
 * from a design package, although the backend places one happily
 * (`swapComponent`'s "No existing faucet — create one",
 * `fixtureManipulation.js`). A package carrying a tub faucet therefore did
 * nothing on any such room, silently.
 *
 * Written component → parent rather than parent → components, because every
 * consumer holds a component and needs its parent: what to offer
 * (`detectFixtureCategories`), whether a tap swaps rather than places
 * (`onTileTap` — the place path has no handler for a component), and which id
 * the swap names (`swapFixture` — the tool anchors on the parent).
 */
const COMPONENT_PARENT_CATEGORY = {
  mirror: 'vanity',
  'wall-light': 'vanity',
  'tub-faucet': 'bathtub',
  // "+ Add Shower Over Tub" is meaningless with no tub to put it over, and
  // the row has to appear while the combo is ABSENT — which is the whole
  // point of it — so presence alone cannot be what surfaces it.
  'tub-shower': 'bathtub',
  // A pendant hangs off a COUNTER PEOPLE SIT AT, which is not a fixture
  // category — an island, or the one seated leg of a peninsula. The parent is
  // resolved by the same `resolvePendantAnchors` the planner places from, so
  // "should this row appear" and "where would a pendant go" can never give
  // different answers.
  'kitchen-pendant': 'pendant-counter',
};

/**
 * The object in `objects` that parents this component category, or null.
 *
 * ONE rule for all three consumers above. `vanity` needs its own classifier
 * rather than a type test: a catalog vanity is a wall-hung lower cabinet, not
 * an object of type `vanity` — an exact-type test dead-ended those (round-2
 * M2). Everything else resolves through its category's own matcher.
 */
const findComponentParent = (categoryKey, objects = []) => {
  const parentKey = COMPONENT_PARENT_CATEGORY[categoryKey];
  if (!parentKey) return null;
  if (parentKey === 'vanity') {
    return (
      findRoomVanityCabinet(objects) ||
      objects.find((o) => o.type === 'vanity') ||
      null
    );
  }
  if (parentKey === 'pendant-counter') {
    const [anchor] = resolvePendantAnchors(objects);
    if (!anchor) return null;
    return objects.find((o) => o.id === anchor.sourceId) || null;
  }
  const parent = FIXTURE_CATEGORIES.find((c) => c.key === parentKey);
  if (!parent) return null;
  return objects.find((o) => matchesCategory(parent, o, objects)) || null;
};

/**
 * True iff the category is allowed to surface for the given room kind.
 * Categories without a `roomKinds` Set are treated as universal (none
 * today — keep this fallback so a future entry can opt into all rooms
 * without listing every kind explicitly).
 *
 * Every entry naming exactly one kind is what keeps the two vocabularies
 * apart: `lower-cabinet` is a bathroom Vanity and a kitchen cabinet run,
 * `sink` and `kitchen-sink` are different catalog types, and neither may be
 * offered under the other room's row.
 */
const categoryAllowedForRoomKind = (category, roomKind) => {
  if (!category.roomKinds) return true;
  return category.roomKinds.has(roomKind);
};

/**
 * Resolve a category's per-object matcher. Custom `match` wins; otherwise
 * fall back to the plain types filter.
 */
const matchesCategory = (category, obj, allObjects = []) => {
  if (typeof category.match === 'function') {
    return category.match(obj, allObjects);
  }
  return category.types.includes(obj.type);
};

/**
 * Given a room's objects array, return which fixture categories should be shown.
 * Includes:
 *   - all categories that have a matching object in the room
 *   - PLUS the always-show categories for the room type (Mockup B requirement)
 *   - PLUS parent-dependent categories whose parent exists
 * Each returned entry includes the category definition + matching objects array
 * (may be empty when the category is shown as "none — tap any to add").
 */
const detectFixtureCategories = (objects = [], roomMeta = {}) => {
  const roomKind = primaryRoomKind(roomMeta);
  const alwaysShow = ALWAYS_SHOW_BY_ROOM_TYPE[roomKind] || new Set();
  // A component shows when its parent is in the room, even with none of the
  // component present — that is what lets its row offer to add one.
  // The vanity keeps `roomHasVanity`, the established presence test, rather
  // than the object lookup: it is what gated mirror and vanity light before,
  // and narrowing it here would be a silent regression for them.
  const hasVanity = roomHasVanityFromObjects(objects);
  const parentDeps = new Set(
    Object.keys(COMPONENT_PARENT_CATEGORY).filter((componentKey) =>
      COMPONENT_PARENT_CATEGORY[componentKey] === 'vanity'
        ? hasVanity
        : Boolean(findComponentParent(componentKey, objects))
    )
  );

  // Restrict the candidate set by room kind FIRST so a kitchen's
  // lower-cabinet doesn't get reported as the bathroom "Vanity"
  // category, and kitchen sinks don't show under the bathroom-only
  // sink category.
  const candidates = FIXTURE_CATEGORIES.filter((category) =>
    categoryAllowedForRoomKind(category, roomKind)
  );

  const shouldShow = (category) => {
    if (alwaysShow.has(category.key)) return true;
    if (parentDeps.has(category.key)) return true;
    return objects.some((obj) => matchesCategory(category, obj, objects));
  };

  return candidates.filter(shouldShow).map((category) => {
    const matchingObjects = objects.filter((obj) =>
      matchesCategory(category, obj, objects)
    );
    return {
      ...category,
      objects: matchingObjects,
      isPresent: matchingObjects.length > 0,
    };
  });
};

/**
 * Check if a room has a shower for the purposes of showing the shower
 * tile section. Two signals:
 *   1. an object whose `type === 'shower'`
 *   2. any wall carrying a shower-tile region in its `texture.regions[]`
 *
 * Walls-with-shower-regions matter because iPhone parity treats a
 * tiled-shower stall (wall texture only, no procedural shower object)
 * as still "having a shower". Without this signal, web hides the shower
 * tile carousel on those rooms.
 */
/**
 * A tub whose fixture group also carries a showerhead / shower controls —
 * a scanned or generated tub/shower combo. Its alcove walls get shower tile
 * (the backend tiles the tub footprint; no floor region, the tub is the floor).
 */
const roomHasTubShowerCombo = (objects = []) =>
  (objects || []).some(
    (obj) =>
      (obj?.type === 'bathtub' || obj?.type === 'tub') &&
      obj.fixtureGroupId &&
      objects.some(
        (o) =>
          o?.fixtureGroupId === obj.fixtureGroupId &&
          (o.type === 'showerhead' || o.type === 'shower-controls')
      )
  );

/**
 * Does this room have a shower FLOOR surface to tile?
 *
 * Deliberately narrower than `roomHasShower`, and a mirror of the backend's
 * `findShowerTilingTarget`: it prefers a real `shower` object, falls back to a
 * tub/shower combo, else nothing. A combo has no shower floor — the tub IS the
 * floor — and a room whose only evidence of a shower is a tiled wall region
 * has no footprint to compute one from, so `apply_shower_tiling` answers
 * "No shower found". Both would render a card whose every tap errors.
 */
const roomHasShowerFloor = (objects = []) =>
  (objects || []).some(
    (obj) => obj?.type === 'shower' || obj?.category?.shower
  );

const roomHasShower = (objects = [], walls = []) => {
  if (objects?.some((obj) => obj?.type === 'shower')) return true;
  if (roomHasTubShowerCombo(objects)) return true;
  if (!Array.isArray(walls)) return false;
  return walls.some((wall) =>
    (wall?.texture?.regions || []).some(
      (r) =>
        r?.source === 'shower' ||
        r?.surfaceType === 'Shower Wall' ||
        r?.surfaceType === 'shower' ||
        r?.kind === 'shower' ||
        r?.regionId?.includes?.('shower')
    )
  );
};

/**
 * Check if a room has countertop/cabinet objects (for showing countertop section).
 */
const roomHasCountertop = (objects = []) =>
  objects.some(
    (obj) => obj.type === 'countertop' || obj.type === 'lower-cabinet'
  );

/**
 * Object types that the Cabinets surface card targets. Includes the
 * end-panel and fridge-surround pieces so a single cabinet-texture swap
 * keeps the cabinetry visually unified instead of leaving the panels in
 * the default finish.
 */
const CABINET_SURFACE_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet',
  'blank-panel',
  'fridge-surround',
  'laundry-surround',
  'oven-surround',
  // Bathroom vanity: same surface as a kitchen lower-cabinet from the
  // rendering side. Without it the Cabinets card would hide on
  // vanity-only rooms.
  'vanity',
]);

/**
 * Check if a room has cabinet-family objects (for showing the cabinets
 * surface section).
 */
const roomHasCabinets = (objects = []) =>
  objects.some((obj) => CABINET_SURFACE_TYPES.has(obj.type));

/**
 * Check if a room has a range (or a legacy `stove`) — what decides whether
 * the panel shows the band behind it (the Stove backsplash tab). Shared so
 * the server's package pricing skips that slot exactly where the panel does.
 */
const roomHasStove = (objects = []) =>
  objects.some((obj) => ['range', 'stove'].includes(obj?.type));

/**
 * Map a UI fixture category key to the backend fixtureType used by
 * placeFixture / addMirrorToVanity / addVanityLight / remove_fixture etc.
 * Returns null for categories that have no place/remove backend (sub-components).
 */
const CATEGORY_TO_BACKEND_FIXTURE_TYPE = {
  vanity: 'vanity',
  toilet: 'toilet',
  bathtub: 'tub',
  shower: 'shower',
  // Feeds `resolveFixtureFit` so the row can say "won't fit · Requires an
  // alcove tub", and `remove_fixture`'s tub-preserving branch.
  'tub-shower': 'tubShower',
  mirror: 'mirror',
  'wall-light': 'vanityLight',
};

/**
 * Confirm-dialog body copy per fixture category.
 */
const REMOVE_CONFIRM_DETAILS = {
  vanity:
    'This will also delete the countertop, sinks, faucets, mirror, and lights.',
  toilet: 'Remove the toilet?',
  bathtub: 'This will also delete the tub faucet and support box.',
  shower:
    'This will also delete the shower door, controls, showerhead, and niche.',
  'tub-shower':
    'This removes the showerhead and valve and the tile on the tub walls. The bath stays.',
  mirror: 'Remove the mirror?',
  'wall-light': 'Remove the vanity light?',
};

module.exports = {
  CAROUSEL_TARGET,
  SURFACE_APPLICATION,
  FIXTURE_CATEGORIES,
  EXCLUDED_OBJECT_TYPES,
  COMPONENT_PARENT_CATEGORY,
  findComponentParent,
  matchesCategory,
  detectFixtureCategories,
  roomHasTubShowerCombo,
  roomHasShowerFloor,
  roomHasShower,
  roomHasCountertop,
  CABINET_SURFACE_TYPES,
  roomHasCabinets,
  roomHasStove,
  CATEGORY_TO_BACKEND_FIXTURE_TYPE,
  REMOVE_CONFIRM_DETAILS,
};
